import { Link, useLocation } from "react-router-dom";
import { type ReactNode } from "react";
import { motion, useReducedMotion } from "motion/react";
import { FabMenu } from "./FabMenu";
import { BrandMark } from "./ui/brand-mark";
import { useMe } from "../api/hooks";
import { useIsAdmin } from "../auth/useIsAdmin";
import { getInitials } from "../lib/initials";

export function AppLayout({ children }: { children: ReactNode }): ReactNode {
  const { data: me } = useMe();
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const isAdmin = useIsAdmin();
  const initials = getInitials(me?.displayName);

  return (
    <div className="min-h-screen bg-background text-on-surface">
      <header className="border-b border-outline-variant/40 bg-surface-container-lowest">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <Link to="/" className="flex items-center gap-3">
            <motion.div
              whileHover={
                reduceMotion ? undefined : { rotate: -4, scale: 1.04 }
              }
              transition={{ type: "spring", stiffness: 420, damping: 28 }}
              className="grid size-9 place-items-center"
            >
              <BrandMark />
            </motion.div>
            <span className="text-headline-md text-primary">Tailspend</span>
          </Link>
          <div className="flex items-center gap-1">
            {isAdmin ? (
              <Link
                to="/admin"
                className="rounded-full px-3 py-1.5 text-label-md text-on-surface-variant transition-colors hover:bg-surface-container hover:text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                Admin
              </Link>
            ) : null}
            <motion.div
              whileTap={reduceMotion ? undefined : { scale: 0.97 }}
              transition={{ type: "spring", stiffness: 500, damping: 32 }}
            >
              <Link
                to="/profile"
                className="flex cursor-pointer items-center gap-2 rounded-full p-1 pr-3 transition-colors hover:bg-surface-container focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                <span
                  aria-hidden="true"
                  className="grid size-8 place-items-center rounded-full bg-primary text-on-primary text-label-md"
                >
                  {initials}
                </span>
                {me ? (
                  <span className="hidden text-label-md text-on-surface-variant sm:inline">
                    {me.displayName}
                  </span>
                ) : (
                  <span className="sr-only">Open profile</span>
                )}
              </Link>
            </motion.div>
          </div>
        </div>
      </header>
      {/*
        Enter-only fade. We deliberately avoid `AnimatePresence mode="wait"`
        here: waiting for the outgoing route to finish its exit before the
        incoming one mounts leaves a blank frame on the page background —
        the intermittent "flash" seen when navigating. Keying the element on
        the pathname remounts it per route, so the new page fades in over the
        previous paint with no gap.
      */}
      <motion.main
        key={location.pathname}
        initial={reduceMotion ? false : { opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
        className="mx-auto max-w-6xl px-6 py-10 sm:px-8"
      >
        {children}
      </motion.main>

      {/*
        Floating "+" action menu: pick a group, then scan / add expense / new
        group. Hidden where it would overlap a page's own primary action —
        the scan page and the create/edit forms (whose Save/Add sits
        bottom-right) — so the fixed FAB never covers a submit button.
      */}
      {showFab(location.pathname) ? <FabMenu /> : null}
    </div>
  );
}

/**
 * The FAB is a fixed bottom-right control, so it must not render on routes
 * whose own primary action lives there. Hide it on the expense /
 * recurring-expense create + edit forms.
 */
function showFab(pathname: string): boolean {
  // New-expense form, top-level (/expenses/new) or nested
  // (/groups/:id/expenses/new) — its own scan/add UI owns the screen.
  if (/(^|\/)expenses\/new$/.test(pathname)) return false;
  // /groups/:id/expenses/:id/edit,
  // /groups/:id/recurring-expenses/new, /…/recurring-expenses/:id
  if (/\/expenses\/[^/]+\/edit$/.test(pathname)) return false;
  if (/\/recurring-expenses(\/|$)/.test(pathname)) return false;
  // Record-payment form: its own Cancel/Record actions own the bottom-right,
  // which the fixed FAB would otherwise overlap (issue: unreachable submit
  // on mobile).
  if (/\/settlements\/new$/.test(pathname)) return false;
  if (pathname === "/groups/new") return false;
  return true;
}
